import { Session, SessionDescriptionHandler } from 'sip.js';

export type VoipCallQualitySnapshotDirection = 'inbound' | 'outbound';

export type VoipCallQualityPlatform = 'web' | 'android' | 'ios';

export interface VoipCallCodeInfo {
  name: string; // The name of the codec in use.
  clockRate: number; // (Hz) The clock rate of the codec.
  channels: number; // Number of audio channels.
}

export interface VoipCallQualitySnapshot {
  eventName: VoipMonitorEvent;
  direction: VoipCallQualitySnapshotDirection;
  platform: VoipCallQualityPlatform;
  timestamp: string;
  personId: string;
  callId: string;
  qualityMetrics: {
    // Network Quality
    jitter?: number; // (ms) Variation in packet arrival time. High values cause choppy audio.
    roundTripTime?: number; // (ms) Latency for a packet to travel to the remote peer and back.
    // Packet Loss
    packetsLost?: number; // (count) Cumulative number of packets lost since the beginning of the call. Only present for inbound snapshots.
    fractionLost?: number; // (ratio, 0.0 to 1.0) The proportion of packets lost in the most recent reporting interval. Only present for inbound snapshots.
    // Bitrate & Data
    bytesReceived?: number; // (bytes) Cumulative bytes received for this stream.
    bytesSent?: number; // (bytes) Cumulative bytes sent for this stream. Only present for outbound snapshots.
    currentBitrate?: number; // (bits per second) The calculated current bitrate of the stream.
  };
  codec: VoipCallCodeInfo;
}

export interface VoipMonitorParams {
  personId: string;
  callId: string;
  session: ExtendedSession;
}

export enum VoipMonitorEvent {
  MONITORING_STARTED = 'monitoringStarted',
  MONITORING_ENDED = 'monitoringEnded',
  ERROR = 'error',
  CONNECTION_QUALITY_CHANGED = 'connectionQualityChanged',
  CALL_QUALITY_SNAPSHOT = 'CallQualitySnapshot',
}

/**
 * Options for WebRTC stats collection
 */
export interface WebRTCStatsOptions {
  tracksInterval?: number;
  snapshotInterval?: number;
  maxMetricsPerCall?: number;
  sendFullCallStats?: boolean;
  enableAlertAdapter?: boolean;
}

export interface BasicStats {
  roundTripTime?: number;
  jitter?: number;
  packetsReceived?: number;
  packetsLost?: number;
  availableBitrate?: number;
}

/**
 * Structure for collected statistics
 */
export interface CollectedStats {
  time: string;
  inboundAudio?: RTCInboundRtpStreamStats;
  outboundAudio?: RTCOutboundRtpStreamStats;
  inboundVideo?: RTCInboundRtpStreamStats;
  outboundVideo?: RTCOutboundRtpStreamStats;
  candidatePair?: RTCIceCandidatePairStats;
  basicStats?: BasicStats;
  raw: RTCStatsReportType[];
}

/**
 * Structure for all call statistics
 */
export interface CallStats {
  metadata: {
    personId: string;
    callId: string;
    startTime?: string;
    endTime: string;
    duration: number;
    codec?: VoipCallCodeInfo;
  };
  summary: {
    mosScore: number;
    averageJitter: number;
    totalPacketsLost: number;
    averageRoundTripTime: number;
    availableBitrate: number;
  };
  stats?: CollectedStats[];
}

interface ExtendedSessionDescriptionHandler extends SessionDescriptionHandler {
  peerConnection: RTCPeerConnection;
}

export interface ExtendedSession extends Session {
  sessionDescriptionHandler: ExtendedSessionDescriptionHandler;
}

/**
 * RTCStats is the base for all types of RTC stats reports
 */
export interface RTCStats {
  timestamp: number;
  type: RTCStatsType;
  id: string;
}

/**
 * RTCStatsType is the type of RTC stats reports
 */
type RTCStatsType =
  | 'codec'
  | 'inbound-rtp'
  | 'outbound-rtp'
  | 'remote-inbound-rtp'
  | 'remote-outbound-rtp'
  | 'media-source'
  | 'media-playout'
  | 'peer-connection'
  | 'data-channel'
  | 'transport'
  | 'candidate-pair'
  | 'local-candidate'
  | 'remote-candidate'
  | 'certificate';

/**
 * RTCInboundRtpStreamStats is the type of RTC stats reports for inbound RTP streams (audio/video received)
 */
export interface RTCInboundRtpStreamStats extends RTCStats {
  bytesReceived: number;
  type: 'inbound-rtp';
  kind: 'audio' | 'video';
  ssrc: number;
  packetsReceived?: number;
  packetsLost?: number;
  jitter?: number;
  framesDecoded?: number;
  audioLevel?: number;
  totalAudioEnergy?: number;
}

/**
 * RTCInboundRtpStreamStats is the type of RTC stats reports for inbound RTP streams (audio/video received)
 */
export interface RTCRemoteInboundRtpStreamStats extends RTCStats {
  type: 'remote-inbound-rtp';
  kind: 'audio' | 'video';
  roundTripTime?: number;
}

/**
 * RTCOutboundRtpStreamStats is the type of RTC stats reports for outbound RTP streams (audio/video sent)
 */
export interface RTCOutboundRtpStreamStats extends RTCStats {
  type: 'outbound-rtp';
  kind: 'audio' | 'video';
  ssrc: number;
  packetsSent?: number;
  bytesSent?: number;
  framesSent?: number;
  framesEncoded?: number;
  codecId?: string;
  mimeType?: string;
}

/**
 * RTCIceCandidatePairStats is the type of RTC stats reports for ICE candidate pairs (network connection)
 */
export interface RTCIceCandidatePairStats extends RTCStats {
  type: 'candidate-pair';
  state: RTCStatsIceCandidatePairState;
  currentRoundTripTime?: number;
  bytesReceived?: number;
  bytesSent?: number;
  availableOutgoingBitrate?: number;
  localCandidateId?: string;
  remoteCandidateId?: string;
}

/**
 * Possible states of an ICE candidate pair
 */
type RTCStatsIceCandidatePairState =
  | 'frozen'
  | 'waiting'
  | 'in-progress'
  | 'failed'
  | 'succeeded';

/**
 * RTCStatsReportType is the type of RTC stats reports for use in collectRTCMetrics
 */
export type RTCStatsReportType =
  | RTCInboundRtpStreamStats
  | RTCOutboundRtpStreamStats
  | RTCIceCandidatePairStats
  | RTCStats;

export interface RTCCodecStats extends RTCStats {
  type: 'codec';
  mimeType?: string;
  clockRate?: number;
  channels?: number;
}
